import { Producto } from '../../../domain/entities/Producto';
import { DatosRemitente, TipoProducto } from './EnvioNacionalModal.types';

export const EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const ALPHANUMERIC_RE = /[^a-zA-Z0-9\s.,#_áéíóúÁÉÍÓÚñÑüÜ-]/g;

export interface TipoVia {
  id: number | string;
  descripcion: string;
  formato_detallado: boolean;
}

export const tipoViaRequiereNumero = (tipoVia: string, tiposVia: TipoVia[]): boolean => {
  const found = tiposVia.find(t => t.descripcion === tipoVia);
  return found?.formato_detallado ?? false;
};

export const normalizarTextoBusqueda = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '');

type CiudadFiltro = { id?: string; label: string; codigoCm?: string };

const yaVisto = (seen: Set<string>, valor: string): boolean => Boolean(valor) && seen.has(valor);

const marcarVisto = (seen: Set<string>, valor: string): void => {
  if (valor) seen.add(valor);
};

/** Una sola fila por id, codigoCm y nombre visible. */
export const deduplicarCiudadesCO = <T extends CiudadFiltro>(ciudades: T[]): T[] => {
  const seenId = new Set<string>();
  const seenCodigo = new Set<string>();
  const seenLabel = new Set<string>();
  const unicas: T[] = [];
  for (const ciudad of ciudades) {
    const id = ciudad.id ? String(ciudad.id) : '';
    const codigo = ciudad.codigoCm ? String(ciudad.codigoCm) : '';
    const label = normalizarTextoBusqueda(ciudad.label);
    if (yaVisto(seenId, id) || yaVisto(seenCodigo, codigo) || yaVisto(seenLabel, label)) {
      continue;
    }
    marcarVisto(seenId, id);
    marcarVisto(seenCodigo, codigo);
    marcarVisto(seenLabel, label);
    unicas.push(ciudad);
  }
  return unicas;
};

/** Filtro local sobre el catálogo ya cargado de GET /ventas/ciudades-co. */
export const filtrarCiudadesCO = <T extends CiudadFiltro>(
  ciudades: T[],
  query: string,
): T[] => {
  const q = normalizarTextoBusqueda(query);
  const unicas = deduplicarCiudadesCO(ciudades);
  if (!q) return unicas;
  const soloDigitos = /^\d+$/.test(q);
  return unicas.filter((c) => {
    const label = normalizarTextoBusqueda(c.label);
    if (label.includes(q)) return true;
    if (!soloDigitos) return false;
    const codigo = String(c.codigoCm ?? '');
    return codigo === q || codigo.startsWith(q);
  });
};

export const parseMilesCO = (value: string): string => value.replaceAll(/\D/g, '');

export const formatMilesCO = (digits: string): string => {
  if (!digits) return '';
  return Number(digits).toLocaleString('es-CO');
};

export const getMaxLenVia = (tipoVia: string, tiposVia: TipoVia[]): number =>
  tipoViaRequiereNumero(tipoVia, tiposVia) ? 15 : 100;

export const buildDireccionCO = (tipoVia: string, via: string, numeroVia: string, tiposVia: TipoVia[]): string => {
  if (!tipoVia && !via) return '';
  const hasNumero = tipoViaRequiereNumero(tipoVia, tiposVia) && numeroVia;
  return hasNumero
    ? `${tipoVia} ${via} # ${numeroVia},`
    : `${tipoVia} ${via},`;
};

export const getProductoEnvioNacional = (tipo: TipoProducto, precio: number): Producto => {
  const datos = tipo === 'paquete'
    ? { id: '1', codigo: 'TRANSMERC', descripcion: 'Transporte - Mercancía' }
    : { id: '2', codigo: 'TRANSMENS', descripcion: 'Transporte - Mensajería' };
  return { ...datos, precioCOP: precio, precioMXN: precio, activo: true };
};

export interface PersonaFormFields {
  nombre: string;
  indicativoPais: string;
  numeroCelular: string;
  codigoPostal: string;
  municipio: string;
  colonia: string;
  direccion: string;
  detalleDireccion: string;
  correo: string;
}

const validateNombre = (val: string): string => {
  if (!val) return 'Campo requerido';
  return val.length > 50 ? 'Máximo 50 caracteres' : '';
};

const validateNumeroCelular = (val: string): string => {
  if (!val) return 'Campo requerido';
  return val.length < 10 ? 'Mínimo 10 dígitos' : '';
};

const validateCodigoPostal = (val: string, errorCodigoPostal?: string | null): string => {
  if (!val) return 'Campo requerido';
  if (val.length < 4) return 'Mínimo 4 dígitos';
  return errorCodigoPostal ?? '';
};

const validateColonia = (val: string): string => {
  if (!val) return 'Campo requerido';
  return val.length > 65 ? 'Máximo 65 caracteres' : '';
};

const validateDireccion = (val: string): string => {
  if (!val) return 'Campo requerido';
  return val.length > 100 ? 'Máximo 100 caracteres' : '';
};

const validateCorreo = (val: string): string => {
  if (!val) return 'Campo requerido';
  return EMAIL_REGEX.test(val) ? '' : 'Correo electrónico no válido';
};

export const getPersonaFieldError = (
  field: keyof PersonaFormFields,
  datos: PersonaFormFields,
  errorCodigoPostal?: string | null
): string => {
  const val = datos[field];
  switch (field) {
    case 'nombre': return validateNombre(val);
    case 'numeroCelular': return validateNumeroCelular(val);
    case 'codigoPostal': return validateCodigoPostal(val, errorCodigoPostal);
    case 'colonia': return validateColonia(val);
    case 'direccion': return validateDireccion(val);
    case 'correo': return validateCorreo(val);
    default: return '';
  }
};

export const isPersonaValida = (datos: PersonaFormFields): boolean => {
  const camposObligatorios: (keyof PersonaFormFields)[] = [
    'nombre', 'numeroCelular', 'codigoPostal', 'municipio', 'colonia', 'direccion', 'correo',
  ];
  return camposObligatorios.every(
    field => datos[field] !== '' && getPersonaFieldError(field, datos, null) === ''
  );
};

// ─── Colombia validators ───────────────────────────────────────────────────

const requerido = (val: string): string => (val ? '' : 'Campo requerido');

const validateNombreCO = (val: string): string => {
  const base = requerido(val);
  if (base) return base;
  return val.length > 50 ? 'Máximo 50 caracteres' : '';
};

const validateCelularCO = (val: string): string => {
  const base = requerido(val);
  if (base) return base;
  if (val.length < 10) return 'Mínimo 10 dígitos';
  return val.length > 12 ? 'Máximo 12 dígitos' : '';
};

const validateIdentificacionCO = (val: string): string =>
  val && val.length > 16 ? 'Máximo 16 caracteres' : '';

const validateViaCO = (val: string, tipoVia: string, tiposVia: TipoVia[]): string => {
  const base = requerido(val);
  if (base) return base;
  const max = getMaxLenVia(tipoVia, tiposVia);
  return val.length > max ? `Máximo ${max} caracteres` : '';
};

const validateNumeroViaCO = (val: string, tipoVia: string, tiposVia: TipoVia[]): string => {
  if (tipoViaRequiereNumero(tipoVia, tiposVia) && !val) return 'Campo requerido';
  return val.length > 15 ? 'Máximo 15 caracteres' : '';
};

export const getPersonaCOFieldError = (
  field: keyof DatosRemitente,
  datos: DatosRemitente,
  tiposVia: TipoVia[] = [],
): string => {
  const val = String(datos[field] ?? '');
  switch (field) {
    case 'nombre': return validateNombreCO(val);
    case 'numeroCelular': return validateCelularCO(val);
    case 'identificacion': return validateIdentificacionCO(val);
    case 'ciudadId':
    case 'tipoVia':
      return requerido(val);
    case 'via': return validateViaCO(val, datos.tipoVia ?? '', tiposVia);
    case 'numeroVia': return validateNumeroViaCO(val, datos.tipoVia ?? '', tiposVia);
    case 'correo': return validateCorreo(val);
    default: return '';
  }
};

export const isPersonaValidaCO = (datos: DatosRemitente, tiposVia: TipoVia[] = []): boolean => {
  const camposBase: (keyof DatosRemitente)[] = ['nombre', 'numeroCelular', 'ciudadId', 'tipoVia', 'via', 'correo'];
  const todosValidos = camposBase.every(f => getPersonaCOFieldError(f, datos, tiposVia) === '');
  const numeroValido = !tipoViaRequiereNumero(datos.tipoVia ?? '', tiposVia) ||
    (!!datos.numeroVia && datos.numeroVia.length <= 15);
  return todosValidos && numeroValido;
};
